import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  FileText,
  Trash2,
  Upload,
  Calendar,
  AlertCircle,
  Lightbulb,
  ChevronDown,
  StickyNote,
  Eye,
  BookOpen,
  User,
  Edit,
} from "lucide-react";
import useAuth from "../../hooks/useAuth";
import {
  fetchExamplesFromBackend,
  deleteExampleFromBackend,
  publishExampleToBackend,
  unpublishExampleFromBackend,
} from "../../lib/exampleStorage";
import useRetryFetch from "../../hooks/useRetryFetch";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import ExamplePreviewDialog from "./ExamplePreviewDialog";

export interface ExampleManagementProps {
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  activityId: string;
  onLoadExample?: (example: any) => void;
  onFollowExample?: (example: any) => void;
  mode?: 'preview' | 'process';
}

/**
 * Component for managing activity examples (view, load, delete, follow)
 * @param mode - 'preview' (full control) or 'process' (view-only with follow)
 * @param onFollowExample - Callback when user clicks Follow button (process mode only)
 */
const ExampleManagement: React.FC<ExampleManagementProps> = ({
  open,
  onOpenChange,
  activityId,
  onLoadExample,
  onFollowExample,
  mode = "preview",
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const { user: currentUser } = useAuth();
  const [examples, setExamples] = useState<any[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [previewExample, setPreviewExample] = useState<any | null>(null);
  const [editExample, setEditExample] = useState<any | null>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState<boolean>(false);
  const [publishExampleId, setPublishExampleId] = useState<string | null>(null);

  const isViewOnly = mode === "process";
  const loadingRef = useRef<boolean>(false);

  // Function to refresh examples list
  const refreshExamples = async () => {
    if (!activityId || loadingRef.current) return;

    try {
      loadingRef.current = true;
      setLoading(true);
      const loadedExamples = await fetchExamplesFromBackend(
        activityId,
        retryFetch,
      );
      setExamples(loadedExamples);
    } catch (error) {
      console.error("Failed to load examples:", error);
      toast.error(t("failed_to_load_examples") || "Failed to load examples");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // Load examples when sheet opens
  useEffect(() => {
    if (open && activityId) {
      refreshExamples();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activityId]);

  const handleDelete = async (exampleId: string) => {
    try {
      await deleteExampleFromBackend(exampleId, retryFetch);
      toast.success(
        t("example_deleted_successfully") || "Example deleted successfully",
      );
      await refreshExamples(); // Refresh list
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Failed to delete example:", error);
      toast.error(t("failed_to_delete_example") || "Failed to delete example");
    }
  };

  const handlePublish = (exampleId: string) => {
    setPublishExampleId(exampleId);
    setPublishDialogOpen(true);
  };

  const confirmPublish = async () => {
    if (!publishExampleId) return;

    try {
      await publishExampleToBackend(
        publishExampleId,
        retryFetch,
        false, // Authors cannot publish anonymously
      );
      toast.success(
        t("example_published_successfully") || "Example published successfully",
      );
      await refreshExamples(); // Refresh list
      setPublishDialogOpen(false);
      setPublishExampleId(null);
    } catch (error) {
      console.error("Failed to publish example:", error);
      toast.error(
        t("failed_to_publish_example") || "Failed to publish example",
      );
    }
  };

  const handleUnpublish = async (exampleId: string) => {
    try {
      await unpublishExampleFromBackend(exampleId, retryFetch);
      toast.success(
        t("example_unpublished_successfully") ||
          "Example unpublished successfully",
      );
      await refreshExamples(); // Refresh list
    } catch (error) {
      console.error("Failed to unpublish example:", error);
      toast.error(
        t("failed_to_unpublish_example") || "Failed to unpublish example",
      );
    }
  };

  const handleLoad = (example: any) => {
    onLoadExample?.(example);
    onOpenChange(false);
  };

  const formatDate = (timestamp: any): string => {
    try {
      // Backend returns Unix timestamp (number), convert to Date
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-yellow-500" />
              {isViewOnly
                ? t("view_examples") || "View Examples"
                : t("manage_examples") || "Manage Examples"}
            </SheetTitle>
            <SheetDescription>
              {isViewOnly
                ? t("view_examples_description") ||
                  "Browse and follow examples created for this activity"
                : t("manage_examples_description") ||
                  "Manage all examples for this activity"}
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-120px)] mt-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mb-4">
                  <div className="w-8 h-8 border-4 border-primary rounded-full border-t-transparent animate-spin"></div>
                </div>
                <p className="text-sm text-gray-500">
                  {t("loading") || "Loading..."}
                </p>
              </div>
            ) : examples.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mb-4 bg-gray-100 rounded-full">
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">
                  {t("no_examples_yet")}
                </h3>
                <p className="max-w-sm text-sm text-gray-500">
                  {t("no_examples_description")}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {examples.map((example) => {
                  const userData = example.data?.userData || example.data;
                  const notes = example.data?.notes || {};
                  const fieldCount = userData
                    ? Object.keys(userData).filter((k) => k !== "notes").length
                    : 0;
                  const notesCount = Object.keys(notes).length;

                  // Determine contributor name
                  let contributorName = "Unknown";
                  const isMyExample =
                    currentUser && example.creator?.email === currentUser.email;

                  if (isMyExample) {
                    contributorName = "My Example";
                  } else if (
                    example.isCreatorAnonymous ||
                    !example.creator?.name
                  ) {
                    contributorName = "Anonymous";
                  } else {
                    contributorName = `${example.creator.name}'s Example`;
                  }

                  return (
                    <div
                      key={example._id}
                      className="p-3 transition-all border border-gray-200 rounded-lg hover:border-primary/30 hover:shadow-sm"
                    >
                      {/* Title and Status */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 break-words line-clamp-2">
                            {example.name}
                          </h4>
                          {example.description && (
                            <p className="mt-1 text-xs text-gray-500 break-words line-clamp-2">
                              {example.description}
                            </p>
                          )}
                        </div>
                        {!isViewOnly && (
                          <div className="flex flex-col gap-1">
                            {example.isPublished ? (
                              <Badge
                                variant="outline"
                                className="flex-shrink-0 text-xs text-green-700 border-green-300 bg-green-50"
                              >
                                {t("published") || "Published"}
                              </Badge>
                            ) : !isMyExample ? (
                              <Badge
                                variant="outline"
                                className="flex-shrink-0 text-xs text-primary border-primary/30 bg-primary/5"
                              >
                                {t("pending_approval") || "Pending Approval"}
                              </Badge>
                            ) : null}
                          </div>
                        )}
                      </div>

                      {/* Contributor Badge */}
                      {!isViewOnly && (
                        <div className="flex items-center gap-1 mb-2">
                          <User className="w-3 h-3 text-gray-500" />
                          <span className="text-xs text-gray-600">
                            {contributorName}
                          </span>
                        </div>
                      )}

                      {/* Metadata Row */}
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="flex-shrink-0 w-3 h-3" />
                          <span className="truncate max-w-[200px]">
                            {formatDate(example.createdOn)}
                          </span>
                        </div>
                        {/* {example.prologue && (
                          <Badge
                            variant="outline"
                            className="flex-shrink-0 text-xs text-purple-700 border-purple-300 bg-purple-50"
                          >
                            ✓ {t("prologue") || "Prologue"}
                          </Badge>
                        )}
                        {example.epilogue && (
                          <Badge
                            variant="outline"
                            className="flex-shrink-0 text-xs text-green-700 border-green-300 bg-green-50"
                          >
                            ✓ {t("epilogue") || "Epilogue"}
                          </Badge>
                        )}
                        {fieldCount > 0 && (
                          <Badge
                            variant="secondary"
                            className="flex-shrink-0 text-xs"
                          >
                            {fieldCount} {t("panes") || "panes"}
                          </Badge>
                        )}
                        {notesCount > 0 && (
                          <Badge
                            variant="outline"
                            className="flex-shrink-0 text-xs"
                          >
                            <StickyNote className="w-3 h-3 mr-1" />
                            {notesCount} {t("notes") || "notes"}
                          </Badge>
                        )} */}
                      </div>

                      {/* Buttons Row */}
                      <div className="flex flex-wrap gap-2">
                        {/* View Preview Button - Always available */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPreviewExample(example)}
                          className="flex-1 h-8 gap-1.5 min-w-[80px]"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span className="text-xs">{t("view_preview")}</span>
                        </Button>

                        {/* Follow Button - Only in process mode and if published */}
                        {isViewOnly &&
                          example.isPublished &&
                          onFollowExample && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => {
                                onFollowExample(example);
                                onOpenChange(false);
                              }}
                              className="h-8 px-3 gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              <BookOpen className="w-3.5 h-3.5" />
                              <span className="text-xs">
                                {t("follow") || "Follow"}
                              </span>
                            </Button>
                          )}

                        {/* Edit Button - Only in preview mode */}
                        {!isViewOnly && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditExample(example)}
                            className="h-8 px-3 gap-1.5"
                          >
                            <Edit className="w-3.5 h-3.5" />
                            <span className="text-xs">{t("edit")}</span>
                          </Button>
                        )}

                        {/* Publish/Unpublish Button - Only for authors in preview mode */}
                        {!isViewOnly && !example.isPublished && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePublish(example._id)}
                            className="h-8 px-3 gap-1.5 text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <Upload className="w-3.5 h-3.5" />
                            <span className="text-xs">{t("publish")}</span>
                          </Button>
                        )}

                        {!isViewOnly && example.isPublished && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnpublish(example._id)}
                            className="h-8 px-3 gap-1.5 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                            <span className="text-xs">{t("unpublish")}</span>
                          </Button>
                        )}

                        {/* Delete Button - Only for authors in preview mode */}
                        {!isViewOnly && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteConfirm(example._id)}
                            className="h-8 px-3 gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span className="text-xs">{t("delete")}</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteConfirm !== null}
        onOpenChange={() => setDeleteConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              {t("delete_example")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_example_confirmation")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Publish Confirmation Dialog */}
      <AlertDialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("publish_example") || "Publish Example"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("publish_example_confirmation") ||
                "Are you sure you want to publish this example? Others will be able to view and follow it."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPublishDialogOpen(false);
                setPublishExampleId(null);
              }}
            >
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmPublish}>
              {t("publish")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Example Preview Dialog */}
      <ExamplePreviewDialog
        example={previewExample}
        activityId={activityId}
        open={previewExample !== null}
        onOpenChange={(open) => !open && setPreviewExample(null)}
        isEditMode={false}
      />

      {/* Example Edit Dialog */}
      <ExamplePreviewDialog
        example={editExample}
        activityId={activityId}
        open={editExample !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditExample(null);
          }
        }}
        isEditMode={true}
        onSaveComplete={refreshExamples}
      />
    </>
  );
};

export default ExampleManagement;
